/**
 * Sense → Outcomes, the screen itself. Slice 60.
 *
 * Split from `OutcomesPage.tsx`, which fetches, so `verify-sense-screens.tsx`
 * can render it; its writes arrive as {@link OutcomeActions} for the same
 * reason — the API client evaluates only inside Vite.
 *
 * ## WHAT THE PAGE IS FOR
 *
 * Saying what a conversion is in this workspace, and recording them. Three
 * cards, in the order a first visit needs them:
 *
 *   1. **What counts as a conversion** — the outcome types, each with a value.
 *      Nothing below works without one, so with none this is the only card.
 *   2. **Upload who converted** — a CSV or XLSX with an email or phone column
 *      and a date column. The Matched tier: each row is a person on a day.
 *   3. **Declare a count** — "14 enquiries this week". The Declared tier: no
 *      person, so it is never credited to a campaign.
 *
 * ## THE UPLOAD SAYS WHAT BECAME OF EVERY ROW
 *
 * Matched, already recorded, matched nobody, unreadable — four numbers that add
 * up to the rows read, shown after each upload and kept in the history. A row
 * that matched nobody is counted, never silently dropped (0042's header); the
 * addresses themselves are not kept, so the page cannot list them and does not
 * pretend to.
 *
 * ## THE COLUMNS ARE SHOWN AS A MAPPING — SLICE 66
 *
 * "A spreadsheet with an Email or Phone column and a Date column" was one
 * sentence a reader had to hold while building the file. It is now the
 * Blueprint's `MappingList`: each column the reader brings, an arrow, what Wend
 * reads it as — the same picture the audience upload draws, so a person who has
 * mapped a list once recognises this one. It describes the importer's rules and
 * no file: there are no sample values, because nothing has been uploaded, and
 * no confidence meter, because nothing has been matched. Every claim on it is
 * one the "How rows are matched" note below already makes.
 *
 * ## MONEY IS TYPED IN RUPEES AND STORED IN PAISE
 *
 * The field takes "1200" or "1,200.50"; the request carries 120000 or 120050.
 * The conversion is here, once, and refuses anything that is not money rather
 * than rounding it into some.
 */
import { useState, type FormEvent } from 'react';

import {
  ACCEPTED_UPLOAD_EXTENSIONS,
  MAX_UPLOAD_BYTES,
  OUTCOME_UPLOAD_MAX_ROWS,
  type CreateOutcomeTypeRequest,
  type DeclaredOutcome,
  type OutcomeType,
  type OutcomeUpload,
  type OutcomesOverview,
  type RecordDeclaredOutcomeRequest,
  type UpdateOutcomeTypeRequest,
} from '@wend/shared';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  Dropzone,
  FormField,
  Input,
  MappingList,
  MappingRow,
  MappingSource,
  Select,
  useToast,
  type DataTableColumn,
} from '@wend/ui';

import { apiFieldErrors, errorMessage } from '../../lib/api/errors';
import { formatCalendarDay, formatCount, formatDateTime, formatRupees } from '../../lib/format';
import { MoreDetail } from '../templates/MoreDetail';

/** "1,200.50" → 120050 paise; null for anything that is not money. */
export function rupeesToPaise(text: string): number | null {
  const cleaned = text.replace(/,/g, '').replace(/^₹\s*/, '').trim();
  if (!/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return Math.round(Number(cleaned) * 100);
}

/**
 * The four writes this screen makes, passed in rather than imported: the API
 * client cannot be imported outside Vite, and a render test needs this screen.
 */
export interface OutcomeActions {
  readonly createType: (body: CreateOutcomeTypeRequest) => Promise<OutcomeType>;
  readonly updateType: (outcomeTypeId: string, body: UpdateOutcomeTypeRequest) => Promise<OutcomeType>;
  readonly upload: (file: File, outcomeTypeId: string) => Promise<OutcomeUpload>;
  readonly declare: (body: RecordDeclaredOutcomeRequest) => Promise<DeclaredOutcome>;
}

export function OutcomesContent({
  overview,
  actions,
  onChange,
}: {
  overview: OutcomesOverview;
  actions: OutcomeActions;
  onChange: () => void;
}) {
  const typeName = new Map(overview.types.map((type) => [type.id, type.name]));
  return (
    <>
      <OutcomeTypes types={overview.types} actions={actions} onChange={onChange} />
      {overview.types.length > 0 && (
        <>
          <UploadConverters types={overview.types} uploads={overview.uploads} typeName={typeName} actions={actions} onChange={onChange} />
          <DeclareCount types={overview.types} declared={overview.declared} typeName={typeName} actions={actions} onChange={onChange} />
        </>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* 1. Types                                                                    */
/* -------------------------------------------------------------------------- */

function OutcomeTypes({
  types,
  actions,
  onChange,
}: {
  types: readonly OutcomeType[];
  actions: OutcomeActions;
  onChange: () => void;
}) {
  const { toast } = useToast();
  const [name, setName] = useState('');
  const [value, setValue] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  // Once a conversion exists, adding another is occasional; the upload below is
  // the recurring job, so the form folds away behind one control.
  const [adding, setAdding] = useState(types.length === 0);

  async function add(event: FormEvent) {
    event.preventDefault();
    const valueMinor = rupeesToPaise(value);
    const next: Record<string, string> = {};
    if (name.trim() === '') next.name = 'Give it a name, like "Order" or "Loan enquiry".';
    if (valueMinor === null) next.valueMinor = 'Type an amount in rupees, like 1200.';
    setErrors(next);
    if (Object.keys(next).length > 0 || valueMinor === null) return;

    setPending(true);
    try {
      const created = await actions.createType({ name: name.trim(), valueMinor });
      toast({ tone: 'success', title: `Added "${created.name}"` });
      setName('');
      setValue('');
      setAdding(false);
      onChange();
    } catch (error) {
      setErrors({ ...apiFieldErrors(error), ...(apiFieldErrors(error).name === undefined ? { form: errorMessage(error) } : {}) });
    } finally {
      setPending(false);
    }
  }

  return (
    <Card data-role="outcome-types">
      <CardHeader>
        <CardTitle>What counts as a conversion</CardTitle>
        <CardDescription>Each one with what it is worth to you.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {types.length > 0 && (
          <ul className="flex flex-col divide-y divide-line rounded-base border border-line">
            {types.map((type) =>
              editing === type.id ? (
                <EditType key={type.id} type={type} actions={actions} onDone={(changed) => { setEditing(null); if (changed) onChange(); }} />
              ) : (
                <li key={type.id} className="flex items-center justify-between gap-3 px-4 py-3" data-role="outcome-type">
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-text">{type.name}</span>
                    {type.waReplyPayload !== null && (
                      <span className="truncate text-[0.8rem] text-muted" data-role="outcome-type-payload">
                        Also when someone presses the WhatsApp button “{type.waReplyPayload}”
                      </span>
                    )}
                  </span>
                  <span className="flex items-center gap-3">
                    <span className="tabular-nums text-muted">{formatRupees(type.valueMinor)} each</span>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(type.id)} aria-label={`Change ${type.name}`}>
                      Change
                    </Button>
                  </span>
                </li>
              ),
            )}
          </ul>
        )}

        {!adding && (
          <div>
            <Button variant="secondary" onClick={() => setAdding(true)}>
              Add a conversion
            </Button>
          </div>
        )}
        {adding && (
        <form className="grid max-w-2xl gap-3 sm:grid-cols-[1fr_10rem_auto] sm:items-end" onSubmit={add} noValidate>
          <FormField label="Name" error={errors.name}>
            <Input value={name} onChange={(event) => setName(event.target.value)} placeholder="Order" maxLength={80} disabled={pending} />
          </FormField>
          <FormField label="Worth (₹)" error={errors.valueMinor}>
            <Input value={value} onChange={(event) => setValue(event.target.value)} placeholder="1200" inputMode="decimal" disabled={pending} />
          </FormField>
          <Button type="submit" variant={types.length === 0 ? 'primary' : 'secondary'} disabled={pending}>
            Add
          </Button>
        </form>
        )}
        {errors.form !== undefined && (
          <p className="text-[0.85rem] text-bad" role="alert">
            {errors.form}
          </p>
        )}
        <MoreDetail summary="What changing a value does">
          <p>
            Conversions already recorded keep the value they were recorded at. A new value applies from the
            next upload or declared count.
          </p>
        </MoreDetail>
      </CardContent>
    </Card>
  );
}

function EditType({
  type,
  actions,
  onDone,
}: {
  type: OutcomeType;
  actions: OutcomeActions;
  onDone: (changed: boolean) => void;
}) {
  const { toast } = useToast();
  const [name, setName] = useState(type.name);
  const [value, setValue] = useState(String(type.valueMinor / 100));
  const [payload, setPayload] = useState(type.waReplyPayload ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);
  const payloadChanged = (payload.trim() === '' ? null : payload.trim()) !== type.waReplyPayload;

  async function save(event: FormEvent) {
    event.preventDefault();
    const valueMinor = rupeesToPaise(value);
    if (valueMinor === null) {
      setErrors({ valueMinor: 'Type an amount in rupees, like 1200.' });
      return;
    }
    setPending(true);
    try {
      const saved = await actions.updateType(type.id, {
        ...(name.trim() === type.name ? {} : { name: name.trim() }),
        ...(valueMinor === type.valueMinor ? {} : { valueMinor }),
        ...(payloadChanged ? { waReplyPayload: payload.trim() === '' ? null : payload.trim() } : {}),
      });
      toast({ tone: 'success', title: `Saved "${saved.name}" at ${formatRupees(saved.valueMinor)}` });
      onDone(true);
    } catch (error) {
      const fields = apiFieldErrors(error);
      setErrors(Object.keys(fields).length > 0 ? fields : { form: errorMessage(error) });
      setPending(false);
    }
  }

  const unchanged = name.trim() === type.name && rupeesToPaise(value) === type.valueMinor && !payloadChanged;
  return (
    <li className="px-4 py-3">
      <form className="grid gap-3 sm:grid-cols-[1fr_10rem_auto_auto] sm:items-end" onSubmit={save} noValidate>
        <FormField label="Name" error={errors.name}>
          <Input value={name} onChange={(event) => setName(event.target.value)} maxLength={80} disabled={pending} />
        </FormField>
        <FormField label="Worth (₹)" error={errors.valueMinor}>
          <Input value={value} onChange={(event) => setValue(event.target.value)} inputMode="decimal" disabled={pending} />
        </FormField>
        <Button type="submit" disabled={pending || unchanged}>
          Save
        </Button>
        <Button type="button" variant="ghost" onClick={() => onDone(false)} disabled={pending}>
          Cancel
        </Button>
        <div className="sm:col-span-4">
          <FormField
            label="WhatsApp button (optional)"
            description="Someone who presses a quick-reply button with exactly this text counts as this conversion, on the day they press it. Copy the button’s text from the template, capitals and all."
            error={errors.waReplyPayload}
          >
            <Input
              value={payload}
              onChange={(event) => setPayload(event.target.value)}
              placeholder="Yes, call me"
              maxLength={256}
              disabled={pending}
            />
          </FormField>
        </div>
      </form>
      {(errors.form ?? errors['']) !== undefined && (
        <p className="mt-2 text-[0.85rem] text-bad" role="alert">
          {errors.form ?? errors['']}
        </p>
      )}
    </li>
  );
}

/* -------------------------------------------------------------------------- */
/* 2. Upload who converted — Matched                                           */
/* -------------------------------------------------------------------------- */

function UploadConverters(props: {
  types: readonly OutcomeType[];
  uploads: readonly OutcomeUpload[];
  typeName: ReadonlyMap<string, string>;
  actions: OutcomeActions;
  onChange: () => void;
}) {
  const [typeId, setTypeId] = useState(props.types[0]?.id ?? '');
  const [file, setFile] = useState<File | null>(null);
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [last, setLast] = useState<OutcomeUpload | null>(null);

  async function upload() {
    if (file === null || typeId === '') return;
    setPending(true);
    setFailure(null);
    try {
      const result = await props.actions.upload(file, typeId);
      setLast(result);
      setFile(null);
      props.onChange();
    } catch (error) {
      setFailure(errorMessage(error));
    } finally {
      setPending(false);
    }
  }

  const columns: DataTableColumn<OutcomeUpload>[] = [
    {
      key: 'file',
      header: 'List',
      cell: (row) => (
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-text">{row.filename}</span>
          <span className="font-mono text-[0.7rem] text-muted">
            {props.typeName.get(row.outcomeTypeId) ?? ''} · {formatDateTime(row.createdAt)}
            {row.coversFrom !== null && row.coversTo !== null && (
              <> · covers {formatCalendarDay(row.coversFrom)} – {formatCalendarDay(row.coversTo)}</>
            )}
          </span>
        </span>
      ),
    },
    { key: 'read', header: 'Rows', align: 'right', cell: (row) => formatCount(row.rowsRead) },
    { key: 'matched', header: 'Recorded', align: 'right', cell: (row) => formatCount(row.rowsMatched) },
    { key: 'duplicate', header: 'Already recorded', align: 'right', cell: (row) => formatCount(row.rowsDuplicate) },
    { key: 'unmatched', header: 'No match', align: 'right', cell: (row) => formatCount(row.rowsUnmatched) },
    { key: 'invalid', header: 'Unreadable', align: 'right', cell: (row) => formatCount(row.rowsInvalid) },
  ];

  return (
    <Card data-role="outcome-upload">
      <CardHeader>
        <CardTitle>Upload who converted</CardTitle>
        <CardDescription>A spreadsheet, one row for each person who converted, read like this:</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <MappingList aria-label="How the columns of the list are read" className="max-w-2xl" data-role="outcome-columns">
          <MappingRow
            arrowLabel="is read as"
            from={<MappingSource column="Email or Phone" emphasis />}
            to={<span className="font-medium text-text">Who converted</span>}
            note="Matched to a person in Audiences, by email first."
            emphasis
          />
          <MappingRow
            arrowLabel="is read as"
            from={<MappingSource column="Date" />}
            to={<span className="font-medium text-text">The day they converted</span>}
            note="2026-10-04 or 04/10/2026, day first."
          />
          <MappingRow
            arrowLabel="is read as"
            from={<MappingSource column="Value" />}
            to={<span className="font-medium text-text">What it was worth</span>}
            note="Optional. Replaces the conversion’s usual worth for that row."
          />
        </MappingList>
        <div className="grid max-w-2xl gap-3">
          <FormField label="Conversion">
            <Select
              value={typeId}
              onValueChange={setTypeId}
              options={props.types.map((type) => ({ value: type.id, label: type.name }))}
              disabled={pending}
            />
          </FormField>
          <Dropzone
            file={file}
            onFileChange={setFile}
            accept={ACCEPTED_UPLOAD_EXTENSIONS}
            maxSizeBytes={MAX_UPLOAD_BYTES}
            disabled={pending}
            label="Drag the list here, or browse"
          />
          <div>
            <Button onClick={() => void upload()} disabled={pending || file === null || typeId === ''}>
              {pending ? 'Matching…' : 'Upload'}
            </Button>
          </div>
          {failure !== null && <p className="text-[0.85rem] text-bad" role="alert">{failure}</p>}
          {last !== null && (
            <p className="text-[0.88rem] text-text" data-role="outcome-upload-result" role="status">
              {formatCount(last.rowsRead)} rows: {formatCount(last.rowsMatched)} recorded
              {last.rowsDuplicate > 0 && `, ${formatCount(last.rowsDuplicate)} already recorded`}
              {last.rowsUnmatched > 0 && `, ${formatCount(last.rowsUnmatched)} with no match in Audiences`}
              {last.rowsInvalid > 0 && `, ${formatCount(last.rowsInvalid)} unreadable`}.
            </p>
          )}
        </div>

        <MoreDetail summary="How rows are matched">
          <p>
            By email first, then phone, to the people in Audiences. A person converts at most once a day for
            each kind of conversion, so uploading the same list twice records nothing new — those rows show as
            already recorded. Dates may be written 2026-10-04 or 04/10/2026 (day first), with or without a time.
            A row is unreadable when it has no usable email or phone, no date, a date after today, or a Value that
            is not money. A row&rsquo;s Value replaces the conversion&rsquo;s usual worth. Up to{' '}
            {formatCount(OUTCOME_UPLOAD_MAX_ROWS)} rows per list. Rows with no match are counted, not kept.
          </p>
        </MoreDetail>

        {props.uploads.length > 0 && (
          <DataTable
            label="Recent uploads"
            columns={columns}
            rows={props.uploads}
            getRowKey={(row) => row.id}
            empty={null}
          />
        )}
      </CardContent>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */
/* 3. Declare a count — Declared                                               */
/* -------------------------------------------------------------------------- */

function DeclareCount(props: {
  types: readonly OutcomeType[];
  declared: readonly DeclaredOutcome[];
  typeName: ReadonlyMap<string, string>;
  actions: OutcomeActions;
  onChange: () => void;
}) {
  const { toast } = useToast();
  const [typeId, setTypeId] = useState(props.types[0]?.id ?? '');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [quantity, setQuantity] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [pending, setPending] = useState(false);

  async function declare(event: FormEvent) {
    event.preventDefault();
    const count = /^\d+$/.test(quantity.trim()) ? Number(quantity.trim()) : Number.NaN;
    const next: Record<string, string> = {};
    if (periodStart === '') next.periodStart = 'Pick the first day.';
    if (periodEnd === '') next.periodEnd = 'Pick the last day.';
    if (!Number.isInteger(count) || count < 1) next.quantity = 'Type a whole number, 1 or more.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setPending(true);
    try {
      await props.actions.declare({ outcomeTypeId: typeId, periodStart, periodEnd, quantity: count });
      toast({ tone: 'success', title: `Declared ${formatCount(count)} × ${props.typeName.get(typeId) ?? ''}` });
      setQuantity('');
      props.onChange();
    } catch (error) {
      const fields = apiFieldErrors(error);
      setErrors(Object.keys(fields).length > 0 ? fields : { form: errorMessage(error) });
    } finally {
      setPending(false);
    }
  }

  const columns: DataTableColumn<DeclaredOutcome>[] = [
    {
      key: 'what',
      header: 'Declared',
      cell: (row) => `${formatCount(row.quantity)} × ${props.typeName.get(row.outcomeTypeId) ?? ''}`,
    },
    {
      key: 'period',
      header: 'Period',
      cell: (row) => `${formatCalendarDay(row.periodStart)} – ${formatCalendarDay(row.periodEnd)}`,
    },
    { key: 'value', header: 'Worth', align: 'right', cell: (row) => formatRupees(row.valueMinor) },
  ];

  return (
    <Card data-role="outcome-declare">
      <CardHeader>
        <CardTitle>Declare a count</CardTitle>
        <CardDescription>When you know how many, but not who. Never credited to a channel or a campaign.</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <form className="grid max-w-3xl gap-3 sm:grid-cols-[1fr_9rem_9rem_7rem_auto] sm:items-end" onSubmit={declare} noValidate>
          <FormField label="Conversion">
            <Select
              value={typeId}
              onValueChange={setTypeId}
              options={props.types.map((type) => ({ value: type.id, label: type.name }))}
              disabled={pending}
            />
          </FormField>
          <FormField label="From" error={errors.periodStart}>
            <Input type="date" value={periodStart} onChange={(event) => setPeriodStart(event.target.value)} disabled={pending} />
          </FormField>
          <FormField label="To" error={errors.periodEnd}>
            <Input type="date" value={periodEnd} onChange={(event) => setPeriodEnd(event.target.value)} disabled={pending} />
          </FormField>
          <FormField label="How many" error={errors.quantity}>
            <Input value={quantity} onChange={(event) => setQuantity(event.target.value)} inputMode="numeric" placeholder="14" disabled={pending} />
          </FormField>
          <Button type="submit" variant="secondary" disabled={pending}>
            Declare
          </Button>
        </form>
        {(errors.form ?? errors['']) !== undefined && (
          <p className="text-[0.85rem] text-bad" role="alert">
            {errors.form ?? errors['']}
          </p>
        )}

        {props.declared.length > 0 && (
          <DataTable
            label="Recent declared counts"
            columns={columns}
            rows={props.declared}
            getRowKey={(row) => row.id}
            empty={null}
          />
        )}
      </CardContent>
    </Card>
  );
}
